import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Site-wide GSAP choreography: hero entrance timeline, scrubbed parallax,
// section slide-ins, CTA zoom, magnetic buttons. Mounted once in App.
export default function GsapFx() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      /* ── Hero entrance ── */
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .from('.hero-badge', { y: 24, opacity: 0, duration: 0.8 })
        .from('.hero-title', { y: 60, opacity: 0, skewY: 2.5, duration: 1.1 }, '-=0.5')
        .from('.hero-sub', { y: 30, opacity: 0, duration: 0.9 }, '-=0.7')
        .from('.hero-ctas > *', { y: 24, opacity: 0, stagger: 0.12, duration: 0.7 }, '-=0.6')
        .from('.hero-stats', { y: 24, opacity: 0, duration: 0.7 }, '-=0.45');

      /* ── Hero scrub parallax (replaces the rAF hook) ── */
      gsap.to('.hero-inner', {
        y: 180,
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 0.4 },
      });
      gsap.to('.hero-cinematic', {
        y: 90,
        scale: 1.06,
        ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 0.6 },
      });

      /* ── Services: alternate slide-in with soft rotation ── */
      gsap.utils.toArray('.service-item').forEach((item, i) => {
        gsap.from(item, {
          x: i % 2 ? 70 : -70,
          opacity: 0,
          rotateZ: i % 2 ? 0.6 : -0.6,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: { trigger: item, start: 'top 82%' },
        });
      });

      /* ── Section titles: rise with a soft clip reveal ── */
      gsap.utils.toArray('.section-title').forEach((el) => {
        gsap.from(el, {
          y: 44,
          opacity: 0,
          clipPath: 'inset(0% 0% 100% 0%)',
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 86%' },
        });
      });

      /* ── Project cards: rise + settle stagger ── */
      ScrollTrigger.batch('.project-card', {
        start: 'top 85%',
        onEnter: (batch) =>
          gsap.from(batch, {
            y: 48,
            opacity: 0,
            scale: 0.97,
            stagger: 0.1,
            duration: 0.9,
            ease: 'power3.out',
            overwrite: true,
          }),
      });

      /* ── CTA: cinematic zoom-in ── */
      gsap.from('.cta-inner', {
        scale: 0.9,
        opacity: 0,
        filter: 'blur(6px)',
        duration: 1.1,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.cta', start: 'top 70%' },
      });
      gsap.fromTo('.cta-cinematic',
        { scale: 1.12 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: '.cta', start: 'top bottom', end: 'bottom bottom', scrub: 0.5 },
        }
      );
    });

    /* ── Magnetic buttons ── */
    const magnets = document.querySelectorAll('.btn-primary, .btn-outline');
    const magnetHandlers = [];
    magnets.forEach((btn) => {
      const xTo = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
      const yTo = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
      const onMove = (e) => {
        const r = btn.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.25);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.35);
      };
      const onLeave = () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.4)' });
      };
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      magnetHandlers.push([btn, onMove, onLeave]);
    });

    return () => {
      ctx.revert();
      magnetHandlers.forEach(([btn, m, l]) => {
        btn.removeEventListener('mousemove', m);
        btn.removeEventListener('mouseleave', l);
      });
    };
  }, []);

  return null;
}
